import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useTranslation } from "../../i18n/LanguageContext";

const ACCENTS = {
  phone: "#0a0a0a",
  email: "#1a1a1a",
  office: "#83588f",
};

const ICONS = {
  phone: Phone,
  email: Mail,
  office: MapPin,
};

const HREFS = {
  phone: "tel:+36306278654",
  email: "mailto:korona.tours555@gmail.com",
  office: "https://www.google.com/maps/search/?api=1&query=H%C3%A9v%C3%ADz+R%C3%A1k%C3%B3czi+utca+7",
};

// OpenStreetMap embed centered on Hévíz, Rákóczi utca 7 (approx. 46.7926, 17.1885)
const OSM_BBOX = "17.180,46.788,17.197,46.798";
const OSM_MARKER = "46.7926,17.18895";
const OSM_EMBED = `https://www.openstreetmap.org/export/embed.html?bbox=${OSM_BBOX}&layer=mapnik&marker=${OSM_MARKER}`;

export default function ContactCTA() {
  const { t } = useTranslation();
  const c = t.contact;
  const channels = Object.entries(c.channels);

  return (
    <section
      id="kapcsolat"
      data-testid="contact-cta-section"
      className="relative py-16 md:py-32 bg-[#ebe2f5] overflow-hidden scroll-mt-24"
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 md:h-24 bg-gradient-to-b from-transparent to-[#dfd3ee]" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#83588f] opacity-[0.10] blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#83588f] opacity-[0.10] blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <p className="text-eyebrow">{c.eyebrow}</p>
          <h2
            data-testid="contact-cta-title"
            className="mt-5 font-serif-display text-[34px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-6xl text-[#0a0a0a] sm:leading-[1.04]"
          >
            {c.titleA}{" "}
            <span className="italic gold-text">{c.titleB}</span>
          </h2>
          <p className="mt-6 text-[#594f5a] leading-relaxed">{c.intro}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {channels.map(([key, ch]) => {
            const Icon = ICONS[key];
            const accent = ACCENTS[key];
            const href = HREFS[key];
            const external = key === "office";
            return (
              <a
                key={key}
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noreferrer" : undefined}
                data-testid={`contact-channel-${key}`}
                className="group relative rounded-3xl bg-[#f5effb] border border-white/5 hover:border-[#0a0a0a]/30 transition-all duration-500 p-6 sm:p-7 md:p-8 flex flex-col hover:-translate-y-1"
              >
                <span
                  className="w-12 h-12 rounded-2xl flex items-center justify-center border"
                  style={{
                    borderColor: `${accent}40`,
                    background: `${accent}10`,
                  }}
                >
                  <Icon
                    size={20}
                    strokeWidth={1.4}
                    style={{ color: accent }}
                  />
                </span>

                <div className="text-[10px] tracking-[0.28em] uppercase text-[#8a7f8f] mt-7">
                  {ch.label}
                </div>
                <div className="mt-2 font-serif-display text-xl md:text-2xl text-[#0a0a0a] leading-tight break-all">
                  {ch.value}
                </div>
                <p className="mt-4 text-sm text-[#8a7f8f] leading-relaxed">
                  {ch.detail}
                </p>

                <span className="mt-auto pt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#594f5a] group-hover:text-[#0a0a0a] transition-colors">
                  {c.open}
                  <ArrowUpRight
                    size={13}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </span>
              </a>
            );
          })}
        </div>

        {/* Map block — Hévíz, Rákóczi utca 7 */}
        <div className="mt-14 md:mt-20" data-testid="contact-map-block">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-5 md:gap-6">
            {/* Address card */}
            <div className="rounded-3xl bg-gradient-to-br from-[#f5effb] to-[#ebe2f5] border border-white/5 p-6 sm:p-8 md:p-10 flex flex-col">
              <p className="text-eyebrow">{c.mapEyebrow}</p>
              <h3 className="mt-4 font-serif-display text-[28px] sm:text-3xl md:text-4xl text-[#0a0a0a] leading-[1.1]">
                {c.mapTitle}
              </h3>
              <p className="mt-4 text-sm md:text-base text-[#594f5a] leading-relaxed max-w-md">
                {c.mapSub}
              </p>

              <div className="mt-7 flex items-start gap-4">
                <span className="w-11 h-11 rounded-2xl border border-[#0a0a0a]/40 bg-[#0a0a0a]/10 flex items-center justify-center shrink-0">
                  <MapPin
                    size={18}
                    className="text-[#0a0a0a]"
                    strokeWidth={1.5}
                  />
                </span>
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#8a7f8f]">
                    {t.contact.channels.office.label}
                  </div>
                  <div className="mt-1 font-serif-display text-xl md:text-2xl text-[#1a1a1a]">
                    {c.mapAddress}
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="relative rounded-3xl overflow-hidden border border-white/5 bg-[#ebe2f5] min-h-[300px] md:min-h-[420px]">
              <iframe
                data-testid="contact-osm-iframe"
                title="Korona Tours&Travel — Hévíz, Rákóczi utca 7"
                src={OSM_EMBED}
                className="absolute inset-0 w-full h-full korona-map"
                loading="lazy"
                style={{ border: 0 }}
              />
              {/* Subtle gold rim + bottom shade for premium feel */}
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#0a0a0a]/15 rounded-3xl" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#ebe2f5]/85 to-transparent" />

              {/* Address label bottom-left */}
              <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 glass-strong rounded-full pl-3 pr-4 py-2 text-xs text-[#1a1a1a]">
                <MapPin size={12} className="text-[#0a0a0a]" />
                <span className="font-serif-display">{c.mapAddress}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-20 text-center">
          <div className="gold-rule mx-auto max-w-md" />
          <p className="mt-8 text-[#594f5a] font-serif-display italic text-2xl md:text-3xl leading-relaxed max-w-2xl mx-auto">
            {c.closing}
          </p>
          <p className="mt-5 text-eyebrow">{c.closingSign}</p>
        </div>
      </div>
    </section>
  );
}
